import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getIcon = (iconName, isActive) => {
    const icons = {
      dashboard: '◫',
      users: '◔',
      building: '▣',
      'user-tie': '◆',
      webphone: '☏',
      history: '↻',
      'sign-out': '⊟',
      bars: '☰',
    };
    return icons[iconName] || '•';
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', adminOnly: true },
    { path: '/webphone', label: 'WebPhone', icon: 'webphone', adminOnly: false },
    { path: '/manager', label: 'Manager', icon: 'users', adminOnly: true },
    { path: '/history', label: 'History', icon: 'history', adminOnly: true },
    { path: '/users', label: 'Users', icon: 'users', adminOnly: true },
    { path: '/customers', label: 'Customers', icon: 'user-tie', adminOnly: true },
    { path: '/skills', label: 'Skills', icon: 'building', adminOnly: true },
    { path: '/groups', label: 'Groups', icon: 'building', adminOnly: true },
  ];

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    setTimeout(() => document.activeElement?.blur(), 0);
  };
  const toggleMobile = () => {
    setMobileOpen(!mobileOpen);
    setTimeout(() => document.activeElement?.blur(), 0);
  };

  const sidebarContent = (
    <div style={{
      ...styles.sidebar,
      width: collapsed ? '60px' : '250px',
    }}>
      <div style={styles.header}>
        {!collapsed && <h2 style={styles.title}>Interactor Platform</h2>}
        {!isMobile && (
          <button onClick={toggleSidebar} style={styles.toggleBtn}>
            {collapsed ? '→' : '←'}
          </button>
        )}
      </div>

      <nav style={styles.nav}>
        {menuItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          const isActive = location.pathname === item.path;
          return (
          <div
              key={item.path}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const target = e.currentTarget;
                target.style.backgroundColor = isActive ? 'rgba(96, 165, 250, 0.2)' : 'transparent';
                target.style.color = isActive ? '#60a5fa' : 'rgba(255, 255, 255, 0.8)';
                navigate(item.path);
                setMobileOpen(false);
                setTimeout(() => {
                  target.blur();
                  document.activeElement?.blur();
                  window.getSelection()?.removeAllRanges();
                }, 0);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                const target = e.currentTarget;
                setTimeout(() => {
                  target.blur();
                }, 0);
              }}
              className={`sidebar-menu-item ${isActive ? 'menuItemActive' : ''}`}
              title={collapsed ? item.label : ''}
              style={{
                justifyContent: collapsed ? 'center' : 'flex-start',
                cursor: 'pointer',
              }}
            >
              <span key={`${item.path}-${isActive}`} style={{
                width: '24px', 
                textAlign: 'center', 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                color: isActive ? '#60a5fa' : 'rgba(255, 255, 255, 0.8)',
              }}>
                {getIcon(item.icon, isActive)}
              </span>
              {!collapsed && <span>{item.label}</span>}
            </div>
          );
        })}
      </nav>

      <div style={styles.footer}>
        {!collapsed && <span style={styles.userInfo}>{user?.username}</span>}
          <div 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const target = e.currentTarget;
            target.style.backgroundColor = 'transparent';
            target.style.color = 'rgba(255, 255, 255, 0.8)';
            handleLogout();
            setTimeout(() => {
              target.blur();
              document.activeElement?.blur();
              window.getSelection()?.removeAllRanges();
            }, 0);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            const target = e.currentTarget;
            setTimeout(() => {
              target.blur();
            }, 0);
          }}
          className="sidebar-menu-item sidebar-logout-btn"
          title="Logout"
          style={{cursor: 'pointer'}}
        >
           <span key="sign-out" style={{
             width: '24px', 
             textAlign: 'center', 
             display: 'inline-flex',
             alignItems: 'center',
             justifyContent: 'center',
             fontSize: '18px',
             color: 'rgba(255, 255, 255, 0.8)',
           }}>
             {getIcon('sign-out', false)}
           </span>
          {!collapsed && <span>Logout</span>}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
         <button onClick={toggleMobile} className="sidebar-mobile-toggle" style={{fontSize: '20px'}}>
            {getIcon('bars', false)}
          </button>
        {mobileOpen && (
          <div onClick={toggleMobile} className="sidebar-overlay" />
        )}
        <div className="sidebar-mobile" style={{
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
          width: '250px',
        }}>
          {sidebarContent}
        </div>
      </>
    );
  }

  return (
    <div className="sidebar-desktop">
      {sidebarContent}
    </div>
  );
};

const styles = {
  sidebar: {
    height: '100vh',
    backgroundColor: '#1a1a2e',
    backgroundImage: 'linear-gradient(180deg, #111827 0%, #1f2937 50%, #374151 100%)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s ease',
    position: 'relative',
  },
  header: {
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    minHeight: '60px',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  title: {
    color: '#60a5fa',
    margin: 0,
    fontSize: '18px',
    whiteSpace: 'nowrap',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textShadow: '0 0 10px rgba(96, 165, 250, 0.3)',
  },
  toggleBtn: {
    background: 'rgba(96, 165, 250, 0.2)',
    border: 'none',
    color: '#60a5fa',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '8px',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  nav: {
    flex: 1,
    padding: '1rem 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    width: '100%',
    textAlign: 'left',
    borderRadius: '0 8px 8px 0',
    marginRight: '8px',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  menuItemHover: {
    backgroundColor: 'rgba(96, 165, 250, 0.1)',
    color: '#60a5fa',
  },
  menuItemActive: {
    backgroundColor: 'rgba(96, 165, 250, 0.2)',
    borderLeft: '3px solid #60a5fa',
    color: '#60a5fa',
    fontWeight: '600',
    boxShadow: '0 0 10px rgba(96, 165, 250, 0.2)',
  },
  icon: {
    minWidth: '24px',
    textAlign: 'center',
    fontSize: '18px',
    color: 'inherit',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    width: '100%',
    textAlign: 'left',
    borderRadius: '0 8px 8px 0',
    marginRight: '8px',
  },
  footer: {
    padding: '1rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  userInfo: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '4px 8px',
    fontStyle: 'italic',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: 'white',
    backgroundColor: 'rgba(239, 68, 68, 0.8)',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    borderRadius: '8px',
    width: '100%',
    transition: 'background-color 0.2s',
    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
  },
};

export default Sidebar;
